import { inject, injectable } from 'inversify'
import { action, computed, makeObservable } from 'mobx'
import { Types } from '@core/ioc/types'
import type { AppLifecycle } from '@core/interfaces/app-lifecycle'
import type { ViewportGateway } from '@core/services/viewport.gateway'
import type { DocumentGateway } from '@core/services/document.gateway'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'
import { ThemeRepository } from './theme.repository'
import { ThemeConfig, type ThemeMode } from './theme.config'

export type { ThemeMode }

@injectable()
export class ThemePresenter implements AppLifecycle {
  @inject(ThemeRepository)
  private themeRepository!: ThemeRepository

  @inject(ThemeConfig)
  private themeConfig!: ThemeConfig

  // A Repository, not I18nPresenter: this wants a lookup, not another
  // Presenter's ViewModel (draft §O). Reading localePm inside a computed is
  // what makes every label below re-resolve when the locale changes.
  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  @inject(Types.IViewportGateway)
  private viewportGateway!: ViewportGateway

  @inject(Types.IDocumentGateway)
  private documentGateway!: DocumentGateway

  private followsSystemPreference = false

  constructor() {
    makeObservable(this, {
      mode: computed,
      effectiveMode: computed,
      viewModel: computed,
      cycle: action,
    })
  }

  get mode(): ThemeMode {
    return this.themeRepository.themeModePm
  }

  // The actually-applied light/dark, resolving 'system' against the live OS
  // preference — drives the .dark class and the toggle's icon.
  get effectiveMode(): 'light' | 'dark' {
    if (this.mode === 'system') {
      return this.viewportGateway.matches(this.themeConfig.darkQuery) ? 'dark' : 'light'
    }
    return this.mode
  }

  /**
   * Everything the toggle renders, resolved here rather than in the component
   * (§3.4/§4.3) — it used to hold its own ICONS/NEXT_LABEL maps and build the
   * aria-label inline. `iconKey` is a key, not a component: which icon library
   * is in use is the view's business, not the presenter's.
   */
  get viewModel() {
    const nextMode = this.themeConfig.nextAfter(this.mode)
    const label = this.i18nRepository.t('theme.toggleLabel', {
      current: this.i18nRepository.t(this.themeConfig.labelKeyFor(this.mode)),
      next: this.i18nRepository.t(this.themeConfig.labelKeyFor(nextMode)),
    })

    return {
      iconKey: this.mode,
      label,
    }
  }

  // Called once from AppPresenter.load().
  initialize() {
    this.themeRepository.restore()
    this.applyToDocument()

    // This is an app-lifetime listener. Register it once while still restoring
    // and applying the current preference on every safe repeated initialize().
    if (this.followsSystemPreference) return
    this.viewportGateway.onChange(this.themeConfig.darkQuery, () => {
      if (this.mode === 'system') {
        this.applyToDocument()
      }
    })
    this.followsSystemPreference = true
  }

  cycle() {
    this.themeRepository.setThemeMode(this.themeConfig.nextAfter(this.mode))
    this.applyToDocument()
  }

  private applyToDocument() {
    this.documentGateway.setRootClass('dark', this.effectiveMode === 'dark')
  }
}
